import { Plus, Minus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

export function FAQs() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqs: FAQ[] = [
    {
      category: 'General',
      question: 'What should I bring to my first appointment?',
      answer: "Please bring your photo ID, insurance card, list of current medications, relevant medical records, and any imaging results. Also, arrive 15 minutes early to complete necessary paperwork if you haven't already submitted it through our patient portal."
    },
    {
      category: 'General',
      question: 'How long does a typical appointment last?',
      answer: 'Initial consultations typically last 60-90 minutes to allow for a thorough evaluation. Follow-up appointments usually last 30-45 minutes, depending on the complexity of your condition.'
    },
    {
      category: 'Insurance',
      question: 'What insurance plans do you accept?',
      answer: 'We accept most major insurance plans including Aetna, Blue Cross Blue Shield, Cigna, Humana, Medicare, and United Healthcare. Please contact our office to verify if we accept your specific insurance plan.'
    },
    {
      category: 'Insurance',
      question: 'Do I need a referral to see a neurologist?',
      answer: 'This depends on your insurance plan. Some plans require a referral from your primary care physician, while others allow direct access to specialists. We recommend checking with your insurance provider or contacting our office for verification.'
    },
    {
      category: 'Treatment',
      question: 'What conditions do you treat?',
      answer: 'We treat a wide range of neurological conditions including headaches, movement disorders, traumatic brain injuries, dysautonomia, neurodevelopmental disorders, and functional neurologic disorders. Visit our Services page for a complete list of conditions we treat.'
    },
    {
      category: 'Treatment',
      question: 'Do you offer telemedicine appointments?',
      answer: 'Yes, we offer telemedicine appointments for certain types of visits and follow-ups when appropriate. Please contact our office to determine if telemedicine is suitable for your specific situation.'
    },
    {
      category: 'Medical Records',
      question: 'How can I access my medical records?',
      answer: 'You can access your medical records through our secure patient portal. You can also request printed copies by completing a medical records request form. Please allow 5-7 business days for processing.'
    },
    {
      category: 'Medical Records',
      question: 'How do I transfer my records from another provider?',
      answer: 'Complete our Medical Records Release form, which authorizes us to request your records from your previous provider. We will handle the transfer process for you.'
    }
  ];

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Find answers to common questions about our services, appointments, and policies. 
          If you don't find what you're looking for, please don't hesitate to contact us.
        </p>
      </div>

      <div className="mb-16">
        {categories.map((category) => (
          <div key={category} className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{category}</h2>
            <div className="space-y-4">
              {faqs
                .filter(faq => faq.category === category)
                .map((faq, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    <button
                      className="w-full px-6 py-4 text-left flex items-center justify-between"
                      onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    >
                      <span className="font-semibold text-gray-900">{faq.question}</span>
                      {openFAQ === index ? (
                        <Minus className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Plus className="h-5 w-5 text-blue-600" />
                      )}
                    </button>
                    {openFAQ === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          We're here to help! Contact our office for any questions not addressed in our FAQs.
        </p>
        <div className="flex justify-center gap-4">
          <Link
            to="/contact"
            className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
          >
            Contact Us
          </Link>
          <Link
            to="/resources/education"
            className="bg-white text-blue-600 border border-blue-600 px-8 py-3 rounded-md font-semibold hover:bg-blue-50 transition-colors"
          >
            View Educational Resources
          </Link>
        </div>
      </div>
    </div>
  );
}